import { REDLINE_PREDICTION_SUCCESS } from '@common/constants/webSockets'
import { IFile } from '@common/interfaces/box'
import {
  ParsedDocument,
  RedlineData,
  RedlineSection,
  SuggestedAction,
} from '@common/interfaces/legalDocument'
import { combineReducers } from 'redux'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction } from '../apiMiddleware'
import { ILoadingData } from '../types'
import * as actions from './actions'

export default combineReducers({
  documents: createLoadingDataReducer<{ data: IFile[] }>(
    [actions.LIST_DOCUMENTS_REQUEST],
    [actions.DELETE_DOCUMENT_REQUEST],
    true,
  ),
  documentData: (
    state: ParsedDocument | null = null,
    action: IAction<ParsedDocument>,
  ): ParsedDocument | null => {
    switch (action.type) {
      case actions.PARSE_LEGAL_DOCUMENT_REQUEST:
        return {
          ...state,
        }
      case actions.PARSE_LEGAL_DOCUMENT_SUCCESS:
        return action.data
      case actions.GET_DOCUMENT_BUFFER_SUCCESS:
        return action.data
      default:
        return state
    }
  },
  redlinesData: createLoadingDataReducer<RedlineData>(
    [actions.GET_REDLINES_REQUEST],
    [
      actions.UPDATE_REDLINE_REQUEST,
      actions.UPDATE_REDLINES_BULK_REQUEST,
      actions.ADD_REDLINE_COMMENT_REQUEST,
      REDLINE_PREDICTION_SUCCESS,
    ],
    true,
    undefined,
    undefined,
    (state: ILoadingData<RedlineData>, action: IAction & { params?: Record<string, unknown> }) => {
      if (action.type === actions.UPDATE_REDLINE_SUCCESS) {
        const params = action.params as {
          redlineId: string
          action: SuggestedAction | 'CLEAR' | null
          modifiedText?: string
        }
        const nextResolution = params.action === 'CLEAR' ? undefined : (params.action ?? undefined)

        // Only the row is live-edited here. resolvedTotal and unresolvedSections are
        // whole-document counts the client can't derive from a loaded page, so the
        // panel refetches the slice after a resolve and lets the server supply them.
        return {
          ...state,
          data: {
            ...state.data,
            data: state.data?.data?.map((redline: RedlineSection) =>
              redline.id === params.redlineId
                ? {
                    ...redline,
                    resolution: nextResolution,
                    resolutionContent:
                      params.action === 'CLEAR'
                        ? undefined
                        : params.modifiedText || redline.suggestedText,
                  }
                : redline,
            ),
          },
        }
      } else if (action.type === actions.UPDATE_REDLINES_BULK_SUCCESS) {
        const params = action.params as {
          redlineIds: string[]
          action: SuggestedAction | null
        }
        const redlineIds = new Set(params.redlineIds)
        return {
          ...state,
          data: {
            ...state.data,
            data: state.data?.data?.map((redline: RedlineSection) =>
              redlineIds.has(redline.id || '')
                ? {
                    ...redline,
                    resolution: params.action ?? undefined,
                    resolutionContent:
                      params.action === SuggestedAction.ACCEPT ? redline.content : undefined,
                  }
                : redline,
            ),
          },
        }
      } else if (action.type === actions.ADD_REDLINE_COMMENT_SUCCESS) {
        const params = action.params as { redlineId: string; comment?: string }
        return {
          ...state,
          data: {
            ...state.data,
            data: state.data?.data?.map((redline: RedlineSection) =>
              redline.id === params.redlineId
                ? {
                    ...redline,
                    comment: params.comment,
                  }
                : redline,
            ),
          },
        }
      } else if (action.type === REDLINE_PREDICTION_SUCCESS) {
        // A failure frame carries `progress.error` and no redline.
        const data = action.data as {
          fileId?: string
          progress: { current: number; total: number; error?: string }
          status?: string
          redlineId?: string
          redline?: Partial<RedlineSection>
        }
        // Frames are broadcast to every UW user; one for another file must not
        // replace the loaded file's progress.
        const loadedFileId = state.data?.predictionProgress?.fileId
        if (loadedFileId && data.fileId !== loadedFileId) return state
        return {
          ...state,
          data: {
            ...state.data,
            predictionProgress: {
              fileId: data.fileId,
              current: data.progress.current,
              total: data.progress.total,
              status: data.status,
              error: data.progress.error,
            },
            data: state.data?.data?.map((redline: RedlineSection) =>
              redline.id === data.redlineId
                ? {
                    ...redline,
                    ...data.redline,
                  }
                : redline,
            ),
          },
        }
      }
      return state
    },
  ),
})
